/**
 * Per-payapp Lien Waiver state.
 *
 * Every covered payapp (G702/draw) has its own waiver status, its own Cumulative? / Thru Curr. Amt?
 * answers and its own contractor/date/amount row. All of that lives in ONE section field,
 * `LienWaiverDraws`, as a JSON array with each entry tagged by `drawId`.
 *
 * Row-tagging rather than per-payapp field names is deliberate: the backend reset transform matches
 * fields by exact name, so suffixed names (`LienWaiverStatus__<drawId>`) would silently fall out of
 * the carry-forward and history logic. One stable field name keeps that working untouched.
 *
 * The pre-multi-draw scalar fields (`LienWaiverStatus`, `IsCumulative`, …) are retained as a mirror
 * of the PRIMARY payapp, so legacy reports hydrate correctly and the backend's history append keeps
 * a value to read. `seedDrawWaiversFromLegacy` performs that hydration.
 *
 * Kept free of component imports so it can be unit-tested directly.
 */

import { LienWaiverStatus } from './lien-waivers-comment.util';
import { FieldType } from '../magic-report.type';

/** The status value persisted by the UI toggle. `Not Received` is the comment engine's `No Waiver`. */
export type LienWaiverStoredStatus = 'Unconditional' | 'Conditional' | 'Not Received';

export interface DrawLienWaiverState {
  drawId: string;
  /**
   * The payapp's display name, carried here so the BACKEND can stamp it onto history rows it
   * archives on reset. The backend only sees this JSON — without the name here it has to infer one
   * from earlier history rows, which is null for a payapp's first archived row.
   */
  drawName: string;
  status: LienWaiverStoredStatus;
  isCumulative: boolean;
  throughCurrentPeriod: boolean;
  /** True once the user hand-edits the contractor/date/amount row, which stops auto-population. */
  isManuallyEdited: boolean;
  contractor: string;
  applicationDate: string;
  applicationNumber: string;
  amount: string;
}

export const DEFAULT_LIEN_WAIVER_STATUS: LienWaiverStoredStatus = 'Conditional';

/** Maps the stored toggle value onto the comment engine's vocabulary. */
export const toCommentStatus = (status: LienWaiverStoredStatus): LienWaiverStatus => (status === 'Not Received' ? 'No Waiver' : status);

/** The line shown in place of the detail row when a payapp has no unconditional waiver. */
export const statusLineFor = (status: LienWaiverStoredStatus): string =>
  status === 'Conditional' ? 'Conditional Waiver Received' : 'No Waiver Received';

export const makeDefaultDrawWaiver = (drawId: string, drawName = ''): DrawLienWaiverState => ({
  drawId,
  drawName,
  status: DEFAULT_LIEN_WAIVER_STATUS,
  isCumulative: false,
  throughCurrentPeriod: false,
  isManuallyEdited: false,
  contractor: '',
  applicationDate: '',
  applicationNumber: '',
  amount: '',
});

const asBool = (value: unknown): boolean => value === true || value === 'true';

const asStatus = (value: unknown): LienWaiverStoredStatus =>
  value === 'Unconditional' || value === 'Not Received' ? value : DEFAULT_LIEN_WAIVER_STATUS;

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Tolerant parse — a malformed or legacy-shaped value yields an empty list rather than throwing. */
export const parseDrawWaivers = (raw?: string): DrawLienWaiverState[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(entry => entry && typeof entry === 'object' && asString(entry.drawId))
      .map(entry => ({
        drawId: asString(entry.drawId),
        drawName: asString(entry.drawName),
        status: asStatus(entry.status),
        isCumulative: asBool(entry.isCumulative),
        throughCurrentPeriod: asBool(entry.throughCurrentPeriod),
        isManuallyEdited: asBool(entry.isManuallyEdited),
        contractor: asString(entry.contractor),
        applicationDate: asString(entry.applicationDate),
        applicationNumber: asString(entry.applicationNumber),
        amount: asString(entry.amount),
      }));
  } catch {
    return [];
  }
};

export const serializeDrawWaivers = (waivers: DrawLienWaiverState[]): string => JSON.stringify(waivers);

/**
 * Reconciles stored per-payapp state against the payapps the report actually covers.
 *
 * Entries for payapps no longer covered are dropped, missing ones are created, and the result is
 * returned in coverage order so the UI and the export agree on which payapp is primary (last).
 *
 * When nothing is stored yet, `legacy` seeds the PRIMARY payapp from the pre-multi-draw scalar
 * fields so an existing single-draw report opens with its saved answers intact.
 */
export const reconcileDrawWaivers = (
  raw: string | undefined,
  drawIds: string[],
  legacy?: Partial<Omit<DrawLienWaiverState, 'drawId'>>,
): DrawLienWaiverState[] => {
  const stored = parseDrawWaivers(raw);
  const byId = new Map(stored.map(entry => [entry.drawId, entry]));
  const primaryDrawId = drawIds.length > 0 ? drawIds[drawIds.length - 1] : undefined;

  return drawIds.map(drawId => {
    const existing = byId.get(drawId);
    if (existing) return existing;
    const seeded = makeDefaultDrawWaiver(drawId);
    if (stored.length === 0 && legacy && drawId === primaryDrawId) {
      return { ...seeded, ...legacy, drawId };
    }
    return seeded;
  });
};

/** True when the reconciled list differs from what is stored, i.e. a write is needed. */
export const drawWaiversChanged = (raw: string | undefined, next: DrawLienWaiverState[]): boolean =>
  serializeDrawWaivers(next) !== (raw || '');

/** The pre-multi-draw scalar fields as the primary payapp's seed; their "No Waiver" is stored as "Not Received". */
export const legacyDrawWaiverSeed = (
  fieldValue: (fieldName: string) => string | undefined,
): Omit<DrawLienWaiverState, 'drawId' | 'drawName'> => {
  const status = fieldValue('LienWaiverStatus');
  return {
    status: status === 'No Waiver' ? 'Not Received' : asStatus(status),
    isCumulative: asBool(fieldValue('IsCumulative')),
    throughCurrentPeriod: asBool(fieldValue('ThroughCurrentPeriod')),
    isManuallyEdited: asBool(fieldValue('LienWaiverIsManuallyEdited')),
    contractor: fieldValue('LienWaiverContractor') || '',
    applicationDate: fieldValue('LienWaiverApplicationDate') || '',
    applicationNumber: fieldValue('LienWaiverApplicationNumber') || '',
    amount: fieldValue('LienWaiverAmount') || '',
  };
};

/**
 * Fills LienWaiverDraws from the scalar fields when a report has none yet (older reports), so it is
 * stored without waiting for the Lien Waivers section to be opened. Otherwise returns the section as is.
 */
export const withSeededDrawWaivers = <T extends { fields?: any[] }>(section: T | undefined, drawIds: string[]): T | undefined => {
  if (!section?.fields || drawIds.length === 0) return section;
  const fields = section.fields;
  const drawsField = fields.find(f => f.fieldName === 'LienWaiverDraws');
  if (drawsField?.value) return section;

  const value = serializeDrawWaivers(
    reconcileDrawWaivers(
      undefined,
      drawIds,
      legacyDrawWaiverSeed(name => fields.find(f => f.fieldName === name)?.value ?? undefined),
    ),
  );
  return {
    ...section,
    fields: drawsField
      ? fields.map(f => (f === drawsField ? { ...f, value } : f))
      : [...fields, { fieldName: 'LienWaiverDraws', fieldType: FieldType.Text, value }],
  };
};

/** The payapps a report covers: its draws, or the single draw of a report from before multi-draw. */
export const resolveCoveredDrawIds = (reportState: any): string[] => {
  if (reportState?.draws?.length) return reportState.draws.map((d: { drawId: string }) => d.drawId);
  return reportState?.drawSummary?.id ? [reportState.drawSummary.id] : [];
};

export interface LienWaiverHistoryEntry {
  /** Identifies the row within one payapp's history. Unique only when paired with `drawId`. */
  periodKey: string;
  /** Absent on rows written before multi-draw; those belong to the primary payapp. */
  drawId?: string;
  drawName?: string;
  status: string;
  isCumulative: boolean;
  throughCurrentPeriod: boolean;
  contractor?: string;
  applicationDate: string;
  applicationNumber: string;
  amount: string;
  updatedAt: string;
}

export const parseLienWaiverHistory = (value?: string): LienWaiverHistoryEntry[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Replaces one payapp's current-period row, leaving every other payapp's history untouched.
 *
 * The natural key is (`drawId`, `periodKey`) — `periodKey` alone collides across payapps, which is
 * what made a single history array unusable for multiple payapps. Rows with no `drawId` predate
 * multi-draw and are matched against `primaryDrawId`.
 */
export const upsertLienWaiverHistory = (
  history: LienWaiverHistoryEntry[],
  entry: LienWaiverHistoryEntry | null,
  match: { drawId: string; periodKey: string; primaryDrawId?: string },
): LienWaiverHistoryEntry[] => {
  const kept = history.filter(existing => {
    const existingDrawId = existing?.drawId || match.primaryDrawId || '';
    return existingDrawId !== match.drawId || existing?.periodKey !== match.periodKey;
  });
  return entry ? [...kept, entry] : kept;
};

/**
 * Groups history by payapp in coverage order, with unassigned rows attributed to the primary.
 * Within a payapp rows keep their saved order: every writer appends, so that is chronological, while
 * periodKey isn't comparable across writers (application numbers, per-payapp sequences, manual keys).
 */
export const sortLienWaiverHistory = (history: LienWaiverHistoryEntry[], drawIds: string[]): LienWaiverHistoryEntry[] => {
  const primaryDrawId = drawIds.length > 0 ? drawIds[drawIds.length - 1] : undefined;
  const rank = (entry: LienWaiverHistoryEntry): number => {
    const index = drawIds.indexOf(entry?.drawId || primaryDrawId || '');
    return index === -1 ? drawIds.length : index;
  };
  return [...history].sort((a, b) => rank(a) - rank(b));
};

/**
 * Splits history into one table per payapp, in coverage order, keeping saved order within each.
 * Unassigned rows belong to the primary; rows for a payapp not in drawIds (hidden, deselected or
 * deleted) are left out but stay saved.
 */
export const groupLienWaiverHistory = <T extends LienWaiverHistoryEntry>(
  history: T[],
  drawIds: string[],
): { drawId: string; rows: T[] }[] => {
  const primaryDrawId = drawIds.length > 0 ? drawIds[drawIds.length - 1] : undefined;
  const groups = drawIds.map(drawId => ({ drawId, rows: [] as T[] }));
  history.forEach(entry => groups.find(g => g.drawId === (entry?.drawId || primaryDrawId))?.rows.push(entry));
  return groups;
};
